import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, InputBase, Tooltip, Typography } from '@mui/material';
import { Check, Close, EditOutlined } from '@mui/icons-material';
import { accountLabel, accountSourceName, NamedAccount } from '../utils/account-label';

interface AccountNameCellProps {
  account: NamedAccount;
  /**
   * Persist a new label. An empty string clears it and falls back to the
   * source's name -- "clear" and "not supplied" are different requests, so
   * this is always called with an explicit value.
   */
  onRename: (displayName: string) => Promise<void> | void;
  /** Rendered under the name; the caller decides what belongs there. */
  secondary?: React.ReactNode;
  disabled?: boolean;
}

/**
 * The account's name, renamable in place.
 *
 * WHY IN PLACE rather than in the edit dialog. Renaming is the one edit these
 * accounts actually need -- Plaid hands over "Non-registered" three times over
 * -- and the useful moment is while looking at the list and realising two rows
 * are indistinguishable. A dialog makes the user leave the comparison to fix
 * it.
 *
 * The original name is shown beside the label whenever a rename is hiding it.
 * Without that, a renamed account cannot be matched back to the real one, and
 * "which of these is the business account" becomes unanswerable again.
 *
 * Flat and ruled to match the accounts list: no card, no pill, colours only
 * through tokens so light and dark both work without a second code path.
 */
export const AccountNameCell: React.FC<AccountNameCellProps> = ({
  account,
  onRename,
  secondary,
  disabled,
}) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const label = accountLabel(account);
  const source = accountSourceName(account);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const startEditing = () => {
    // Seeded with the CURRENT label, not with display_name: the user is
    // editing what they can see. Typing over "Chequing" and saving stores it
    // as a label, which is what they meant.
    setValue(label);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setValue('');
  };

  const commit = async () => {
    const next = value.trim();
    // Renaming an account back to its source name is a request to CLEAR the
    // label, not to store a duplicate of it. Storing it would leave the row
    // permanently marked as renamed with nothing to show for it.
    const toSave = next === account.name ? '' : next;
    if (toSave === (account.display_name ?? '')) {
      cancel();
      return;
    }
    setSaving(true);
    try {
      await onRename(toSave);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <InputBase
          inputRef={inputRef}
          value={value}
          disabled={saving}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void commit();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
          inputProps={{ 'aria-label': `Rename ${label}`, maxLength: 100 }}
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: '0.95rem',
            fontWeight: 600,
            // A hairline under the text, not a boxed field: the row is ruled,
            // and a bordered input inside it reads as a second container.
            borderBottom: '1px solid var(--accent)',
            '& input': { p: 0 },
          }}
        />
        <IconButton
          size="small"
          aria-label="Save name"
          disabled={saving}
          onClick={() => void commit()}
        >
          <Check fontSize="small" />
        </IconButton>
        <IconButton size="small" aria-label="Cancel rename" disabled={saving} onClick={cancel}>
          <Close fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 0,
          // The pencil stays out of the way until the row is engaged with, so
          // a list of thirty accounts is not thirty icons.
          '&:hover .fw-rename, &:focus-within .fw-rename': { opacity: 1 },
        }}
      >
        <Typography
          variant="body1"
          fontWeight="medium"
          title={label}
          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {label}
        </Typography>
        {!disabled && (
          <Tooltip title="Rename">
            <IconButton
              className="fw-rename"
              size="small"
              aria-label={`Rename ${label}`}
              onClick={startEditing}
              sx={{
                opacity: 0,
                transition: 'opacity 0.12s ease',
                // Always visible to the keyboard, which never hovers.
                '&:focus-visible': { opacity: 1 },
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                // Touch has no hover state either.
                '@media (hover: none)': { opacity: 1 },
              }}
            >
              <EditOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {source && (
        // The bank's own name, kept quiet but kept. Three accounts really are
        // called "Non-registered"; once renamed, this line is the only thing
        // that says which is which.
        <Typography
          variant="caption"
          color="text.secondary"
          title={source}
          sx={{
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {source}
        </Typography>
      )}
      {secondary}
    </Box>
  );
};
